import { Dayjs } from "dayjs";
import { useUserRole } from "../../utils/hooks/useUserRole";

/**
 * Mirrors `PivotConstants.MAX_SPAN_DAYS` on the backend, which every /pivot endpoint checks
 * as `(date_to − date_from) in whole days > MAX_SPAN_DAYS` — an exclusive difference, so a
 * 366-day span (367 inclusive days) is the widest range that is accepted, not rejected.
 */
export const PIVOT_MAX_SPAN_DAYS = 366;

/**
 * The visits view carries a second, tighter cap that applies only when no `branch_code`
 * narrows the scope: `buildVisitPivot` holds one cell per (store × day), so a
 * head-office-wide span is an out-of-memory risk before anything is rendered or exported.
 *
 * It is NOT export-only. The backend checks it inside `buildVisitPivot`, which both
 * `GET /v1/pivot/visits` and `GET /v1/pivot/export?view=visits` go through, and rejects
 * either with `PIVOT_VISITS_SPAN_TOO_LONG` before touching the database. The value is the
 * backend's `MAX_GRID_MAX_DAYS`, the same span the on-screen grid is capped at.
 */
export const PIVOT_VISITS_MAX_SPAN_DAYS = 92;

/** Whole days between the two ends, matching the backend's exclusive `span_days`. */
export const pivotSpanDays = (dateFrom: Dayjs, dateTo: Dayjs) =>
  dateTo.startOf("day").diff(dateFrom.startOf("day"), "day");

/**
 * Whether this user's requests are pinned to a single branch by their token.
 *
 * Mirrors the backend's `getQueryUserEntity`: only MASTER and HO_ACCOUNTING may choose a
 * branch — every other role, **ADMIN included**, has `branch_code` overwritten with its own
 * branch server-side, so the query parameter cannot widen the scope for them.
 *
 * Two things depend on getting this right, and both were wrong while the page guessed
 * `admin || master || ho`:
 *
 * * the branch dropdown — ADMIN was offered a choice the backend then ignored, so the page
 *   showed one branch's numbers under another branch's name, with nothing to signal it;
 * * the visits-span guard below — a branch-locked user never sets `branchCode`, so the page
 *   read their scope as head-office-wide and blocked them from a request the backend would
 *   have served, telling them to pick a branch they have no control for.
 *
 * `scope.branch_locked` on every pivot response carries the same answer from the server and
 * can be used to assert this once data has loaded; this hook is what the page needs before
 * the first response arrives.
 */
export const usePivotBranchLocked = () => {
  const { isMaster, isHeadOfficeAccounting } = useUserRole();
  return !(isMaster || isHeadOfficeAccounting);
};

/**
 * True when the visits view — on screen or as CSV — would hit the backend's span guard.
 * Mirrors the backend condition 1:1: it fires only for a caller who can widen the scope and
 * did not, so `branchLocked` short-circuits it. `branchCode === "all"` is the page's
 * sentinel for "no branch_code sent".
 */
export const isVisitsSpanBlocked = (
  branchLocked: boolean,
  branchCode: string,
  dateFrom: Dayjs,
  dateTo: Dayjs,
) =>
  !branchLocked &&
  branchCode === "all" &&
  pivotSpanDays(dateFrom, dateTo) > PIVOT_VISITS_MAX_SPAN_DAYS;

/**
 * The PWA visit-time endpoints (`/pivot/route-days`, `/pivot/visit-times`) refuse a range
 * longer than 31 days with a branch, and longer than 7 days across every branch (head
 * office with no branch picked). Both count both ends.
 */
export const PIVOT_ROUTE_TIME_MAX_DAYS = 31;
export const PIVOT_ROUTE_TIME_HO_MAX_DAYS = 7;

export const ROUTE_TIME_HO_SPAN_MSG =
  `ดูทุกสาขาได้ครั้งละไม่เกิน ${PIVOT_ROUTE_TIME_HO_MAX_DAYS} วัน — ` +
  `เลือกสาขาเพื่อดูได้ถึง ${PIVOT_ROUTE_TIME_MAX_DAYS} วัน`;

/**
 * Which span cap this scope would hit on the visit-time endpoints: `max` (over 31 days),
 * `ho` (over 7 days with no branch, for a caller who can widen the scope), or null.
 * A branch-locked caller is always scoped to a branch server-side, so only `max` applies.
 */
export type RouteTimeSpanBlock = "max" | "ho" | null;

export const routeTimeSpanBlock = (
  branchLocked: boolean,
  branchCode: string,
  dateFrom: Dayjs,
  dateTo: Dayjs,
): RouteTimeSpanBlock => {
  const days = pivotSpanDays(dateFrom, dateTo) + 1;
  if (days > PIVOT_ROUTE_TIME_MAX_DAYS) return "max";
  if (!branchLocked && branchCode === "all" && days > PIVOT_ROUTE_TIME_HO_MAX_DAYS) return "ho";
  return null;
};
